import { useInfiniteQuery } from '@tanstack/react-query';
import { Rocket } from 'lucide-react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api, qs } from '../../api/client';
import { useProject } from '../../api/hooks';
import { useIsAdmin } from '../../api/session';
import { useCrumbs } from '../../layout/crumbs';
import { Badge, StateBadge } from '../../ui/Badge';
import { LinkButton } from '../../ui/Button';
import { Alert, EmptyState, QueryView } from '../../ui/feedback';
import { Select } from '../../ui/forms';
import { LoadMore, PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';

interface Row {
  id: string;
  displayId: string;
  title: string;
  state: string;
  environment: string | null;
  isCompletionTarget: boolean;
  executor: { kind: string; name: string } | null;
  tickets: string[];
  candidateNumber: number | null;
  approval: { displayId: string; state: string; useBy: string | null; consumed: boolean } | null;
  latestAttempt: { number: number; state: string; provenance: string | null } | null;
  reservedTickets: number;
  updatedAt: string;
}

/** WF-10/11: release lifecycle is separate from ticket status. */
export function ReleasesPage() {
  const { key } = useParams();
  const project = useProject(key);
  const admin = useIsAdmin();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const state = params.get('state') ?? '';
  usePageTitle(project.data ? `${project.data.name} · Releases` : null);
  useCrumbs([{ label: project.data?.name ?? key ?? '', to: `/projects/${key}` }, { label: 'Releases' }]);
  const q = useInfiniteQuery({
    queryKey: ['releases', key, state],
    queryFn: ({ pageParam }) =>
      api.get<{ items: Row[]; nextCursor: string | null }>(
        `/projects/${key}/releases${qs({ state, cursor: pageParam })}`,
      ),
    initialPageParam: '',
    getNextPageParam: (l) => l.nextCursor ?? undefined,
  });
  return (
    <div className="page">
      <PageHeader
        title="Releases"
        subtitle="Each release binds ready tickets to an exact revision and environment. Every attempt needs its own admin approval."
        actions={
          admin &&
          !project.data?.archived && (
            <LinkButton to={`/projects/${key}/releases/new`} variant="primary">
              Create release
            </LinkButton>
          )
        }
      />
      <div className="filter-bar">
        <Select
          aria-label="Release state"
          value={state}
          onChange={(e) => setParams(e.target.value ? { state: e.target.value } : {}, { replace: true })}
        >
          <option value="">All states</option>
          {[
            'draft',
            'awaiting_approval',
            'approved',
            'deploying',
            'unknown',
            'failed',
            'succeeded',
            'cancelled',
          ].map((s) => (
            <option key={s} value={s}>
              {s.replace('_', ' ')}
            </option>
          ))}
        </Select>
      </div>
      <QueryView query={q}>
        {(d) => {
          const items = d.pages.flatMap((p) => p.items);
          const attention = items.filter(
            (r) => r.state === 'unknown' || r.state === 'failed' || r.state === 'deploying',
          );
          if (!items.length)
            return (
              <EmptyState icon={Rocket} title="No releases yet">
                Tickets in Pending release can be bundled into a release candidate.
              </EmptyState>
            );
          return (
            <>
              {attention.length > 0 && (
                <Alert tone="warning" title="Delivery needs attention">
                  <ul>
                    {attention.map((r) => (
                      <li key={r.id}>
                        <Link to={`/projects/${key}/releases/${r.displayId}`}>{r.displayId}</Link> is{' '}
                        {r.state}
                        {r.state !== 'failed'
                          ? ' — the environment is locked until reconciled'
                          : ' — reservations are held until retry or cancellation'}
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Release</th>
                      <th>Environment</th>
                      <th>Tickets</th>
                      <th>Executor</th>
                      <th>State</th>
                      <th>Approval</th>
                      <th>Latest attempt</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((r) => (
                      <tr
                        key={r.id}
                        className="clickable"
                        onClick={() => navigate(`/projects/${key}/releases/${r.displayId}`)}
                      >
                        <td>
                          <Link
                            to={`/projects/${key}/releases/${r.displayId}`}
                            className="mono"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {r.displayId}
                          </Link>
                          <div className="primary-cell">{r.title}</div>
                        </td>
                        <td>
                          {r.environment}
                          {r.isCompletionTarget && (
                            <div>
                              <Badge tone="success" dot={false}>
                                Completion target
                              </Badge>
                            </div>
                          )}
                        </td>
                        <td className="small mono">
                          {r.tickets.join(', ')}
                          <div className="muted">{r.reservedTickets} reserved</div>
                        </td>
                        <td className="small">
                          {r.executor
                            ? `${r.executor.name}${r.executor.kind === 'ai_service' ? ' (AI)' : ''}`
                            : '—'}
                        </td>
                        <td>
                          <StateBadge state={r.state} />
                        </td>
                        <td className="small">
                          {r.approval ? (
                            <>
                              {r.approval.displayId} ·{' '}
                              <StateBadge
                                state={r.approval.consumed ? 'released' : r.approval.state}
                                label={r.approval.consumed ? 'Used' : undefined}
                              />
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="small">
                          {r.latestAttempt ? (
                            <>
                              ATT-{r.latestAttempt.number} · <StateBadge state={r.latestAttempt.state} />
                              {r.latestAttempt.provenance && (
                                <StateBadge state={r.latestAttempt.provenance} />
                              )}
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="small">
                          <Timestamp value={r.updatedAt} relative />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <LoadMore
                hasMore={!!q.hasNextPage}
                onClick={() => q.fetchNextPage()}
                loading={q.isFetchingNextPage}
              />
            </>
          );
        }}
      </QueryView>
    </div>
  );
}
